import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Aviso,
  Botao,
  Campo,
  CampoDeMoeda,
  CampoDeSelecao,
  CampoDeTexto,
  Modal,
} from '../../componentes/ui';
import { mensagemDoErro } from '../../servicos/erros';
import { lerValorDigitado, moedaParaCampo } from '../../servicos/formato';
import {
  ACOES,
  AJUDA_DA_ACAO,
  AJUDA_DO_GATILHO,
  CHAVE_DAS_AUTOMACOES,
  FUNCOES_SUGERIDAS,
  GATILHOS,
  ROTULO_DA_ACAO,
  ROTULO_DO_GATILHO,
  ROTULO_DO_SERVICO,
  ROTULO_DO_TIPO_DE_ATIVIDADE,
  SERVICOS_DA_AUTOMACAO,
  TAG_DO_SERVICO,
  TIPOS_DE_ATIVIDADE,
  VARIAVEIS_DO_TEXTO,
  acaoEmBranco,
  carregarOpcoesDasAutomacoes,
  criarAutomacao,
  descreverAutomacao,
  editarAutomacao,
  interpretarAutomacao,
  type AcaoDaRegra,
  type DefinicaoDaRegra,
  type ParaQuem,
  type ServicoDaAutomacao,
  type TipoDeAcao,
  type TipoDeGatilho,
} from '../../servicos/automacoes';
import { ErroDaApi } from '../ErroDaApi';

/** O que o editor abre: uma regra existente (com `id`) ou uma nova. */
export interface RascunhoDeAutomacao {
  readonly id?: string;
  readonly nome: string;
  readonly definicao: DefinicaoDaRegra;
  readonly ativa: boolean;
  /** O texto que a pessoa escreveu para a IA, quando a regra nasceu dele. */
  readonly pedido: string | null;
  /** O que a IA disse ter entendido — fica no topo do editor. */
  readonly entendimento?: string;
}

/**
 * Montar ou ajustar uma automação nos campos — o "Criar sem IA", e o
 * "Editar" de qualquer regra. Embaixo, como o Deca vai executar, escrito pelo
 * servidor a partir dos campos: é o que vale, e não o texto de quem pediu.
 */
export function EditorDeAutomacao(props: {
  readonly rascunho: RascunhoDeAutomacao | null;
  readonly aoFechar: () => void;
  /** Na aba Automações (as do CRM): sem "Enviar um e-mail", que é da página do e-mail. */
  readonly semEmail?: boolean;
}): JSX.Element {
  const queryClient = useQueryClient();
  const aberto = props.rascunho !== null;
  const [nome, setNome] = useState('');
  const [definicao, setDefinicao] = useState<DefinicaoDaRegra | null>(null);
  const [ativa, setAtiva] = useState(true);
  const [pedido, setPedido] = useState<string | null>(null);
  const [entendimento, setEntendimento] = useState<string | undefined>(undefined);
  const [correcao, setCorrecao] = useState('');
  const [valorMinimo, setValorMinimo] = useState('');

  // Cada abertura recomeça do rascunho recebido.
  useEffect(() => {
    if (props.rascunho === null) return;
    setNome(props.rascunho.nome);
    setDefinicao(props.rascunho.definicao);
    setAtiva(props.rascunho.ativa);
    setPedido(props.rascunho.pedido);
    setEntendimento(props.rascunho.entendimento);
    setCorrecao('');
    setValorMinimo(moedaParaCampo(props.rascunho.definicao.condicoes.valorMinimo));
  }, [props.rascunho]);

  const opcoes = useQuery({
    queryKey: [...CHAVE_DAS_AUTOMACOES, 'opcoes'],
    queryFn: carregarOpcoesDasAutomacoes,
    enabled: aberto,
  });
  const colunas = useMemo(
    () =>
      (opcoes.data?.quadros ?? []).flatMap((q) =>
        q.colunas.map((c) => ({ valor: c.id, rotulo: `${q.nome} › ${c.nome}` })),
      ),
    [opcoes.data],
  );

  // A descrição do servidor, um instante depois de a pessoa parar de mexer.
  const [paraDescrever, setParaDescrever] = useState<DefinicaoDaRegra | null>(null);
  useEffect(() => {
    const t = setTimeout(() => setParaDescrever(definicao), 400);
    return () => clearTimeout(t);
  }, [definicao]);
  const descricao = useQuery({
    queryKey: [...CHAVE_DAS_AUTOMACOES, 'descricao', paraDescrever],
    queryFn: () => descreverAutomacao(paraDescrever!),
    enabled: aberto && paraDescrever !== null,
    retry: false,
  });

  const salvar = useMutation({
    mutationFn: () => {
      const dados = { nome: nome.trim(), definicao: definicao!, ativa, pedido };
      return props.rascunho?.id === undefined
        ? criarAutomacao(dados)
        : editarAutomacao(props.rascunho.id, dados);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CHAVE_DAS_AUTOMACOES });
      props.aoFechar();
    },
  });

  const corrigir = useMutation({
    mutationFn: () =>
      interpretarAutomacao({
        texto: correcao.trim(),
        atual: { nome: nome.trim(), definicao: definicao! },
      }),
    onSuccess: (r) => {
      if (!r.ok) return;
      setNome(r.nome);
      setDefinicao(r.definicao);
      setEntendimento(r.entendimento);
      setValorMinimo(moedaParaCampo(r.definicao.condicoes.valorMinimo));
      setPedido((p) => [p, correcao.trim()].filter((t) => t !== null && t !== '').join('\n'));
      setCorrecao('');
    },
  });

  const d = definicao;
  const mudar = (parcial: Partial<DefinicaoDaRegra>): void =>
    setDefinicao((atual) => (atual === null ? atual : { ...atual, ...parcial }));
  const mudarAcao = (i: number, acao: AcaoDaRegra): void =>
    mudar({ acoes: d!.acoes.map((a, j) => (j === i ? acao : a)) });
  const doCard = d?.gatilho.tipo === 'CARD_NA_COLUNA';

  return (
    <Modal
      aberto={aberto}
      aoFechar={props.aoFechar}
      titulo={props.rascunho?.id === undefined ? 'Nova automação' : 'Editar automação'}
      largura="ampla"
      rodape={
        <>
          <Botao variante="secundario" onClick={props.aoFechar}>
            Cancelar
          </Botao>
          <Botao
            carregando={salvar.isPending}
            disabled={nome.trim() === '' || d === null || descricao.isError}
            onClick={() => salvar.mutate()}
          >
            Salvar
          </Botao>
        </>
      }
    >
      {d === null ? null : (
        <div className="flex flex-col gap-6">
          {entendimento !== undefined && (
            <Aviso tom="informacao" titulo="A IA entendeu assim">
              {entendimento}
            </Aviso>
          )}

          <div className="grid gap-4 sm:grid-cols-[1fr_auto] sm:items-end">
            <Campo
              rotulo="Nome"
              maxLength={120}
              value={nome}
              placeholder="Ex.: Concluída vai para Fechados"
              onChange={(e) => setNome(e.target.value)}
            />
            <Marcar rotulo="Ligada" marcada={ativa} aoMudar={setAtiva} />
          </div>

          <Bloco titulo="Quando">
            <div className="grid gap-4 sm:grid-cols-2">
              <CampoDeSelecao
                rotulo="Gatilho"
                value={d.gatilho.tipo}
                ajuda={AJUDA_DO_GATILHO[d.gatilho.tipo]}
                onChange={(e) => {
                  const tipo = e.target.value as TipoDeGatilho;
                  mudar({
                    gatilho: tipo === 'CARD_NA_COLUNA' ? { tipo, colunaId: null } : { tipo },
                    // O gatilho de card não vem de serviço nem tem valor.
                    ...(tipo === 'CARD_NA_COLUNA' ? { condicoes: {} } : {}),
                  });
                  if (tipo === 'CARD_NA_COLUNA') setValorMinimo('');
                }}
                opcoes={GATILHOS.map((g) => ({ valor: g, rotulo: ROTULO_DO_GATILHO[g] }))}
              />
              {doCard && (
                <CampoDeSelecao
                  rotulo="Coluna"
                  value={d.gatilho.colunaId ?? ''}
                  onChange={(e) =>
                    mudar({ gatilho: { tipo: 'CARD_NA_COLUNA', colunaId: e.target.value || null } })
                  }
                  opcoes={[{ valor: '', rotulo: 'Escolha a coluna' }, ...colunas]}
                />
              )}
            </div>
          </Bloco>

          {!doCard && (
            <Bloco
              titulo="Se"
              nota="Opcional. Sem nada marcado, vale para qualquer serviço e valor."
            >
              <div className="flex flex-col gap-4">
                <div className="flex flex-wrap gap-x-6 gap-y-2">
                  {SERVICOS_DA_AUTOMACAO.map((s) => (
                    <Marcar
                      key={s}
                      rotulo={`No ${ROTULO_DO_SERVICO[s]}`}
                      marcada={d.condicoes.servicos?.includes(s) === true}
                      aoMudar={(marcada) => {
                        const atuais = d.condicoes.servicos ?? [];
                        const servicos: ServicoDaAutomacao[] = marcada
                          ? [...atuais, s]
                          : atuais.filter((x) => x !== s);
                        mudar({ condicoes: { ...d.condicoes, servicos } });
                      }}
                    />
                  ))}
                </div>
                <div className="grid gap-4 sm:grid-cols-2 sm:items-end">
                  <CampoDeMoeda
                    rotulo="Valor a partir de"
                    ajuda="Fica de fora a operação que não informa valor (o Recorrente não informa)."
                    value={valorMinimo}
                    onChange={(texto) => {
                      setValorMinimo(texto);
                      const valor = lerValorDigitado(texto);
                      const { valorMinimo: _, ...resto } = d.condicoes;
                      mudar({
                        condicoes:
                          valor === null || valor <= 0 ? resto : { ...resto, valorMinimo: valor },
                      });
                    }}
                  />
                  <Marcar
                    rotulo="Só negócio novo"
                    ajuda="No Recorrente, ignora o cálculo do mês de quem já tem contrato fechado e a renovação do ano."
                    marcada={d.condicoes.somenteNegocioNovo === true}
                    aoMudar={(marcada) => {
                      const { somenteNegocioNovo: _, ...resto } = d.condicoes;
                      mudar({
                        condicoes: marcada ? { ...resto, somenteNegocioNovo: true } : resto,
                      });
                    }}
                  />
                </div>
              </div>
            </Bloco>
          )}

          <Bloco
            titulo="Então"
            nota="Em ordem. Quando uma ação não muda nada, as seguintes não rodam."
          >
            <ol className="flex flex-col gap-4">
              {d.acoes.map((acao, i) => (
                <li key={i} className="rounded-card border border-borda p-4">
                  <div className="mb-3 flex flex-wrap items-end gap-3">
                    <span className="valor-fiscal pb-2.5 text-sm text-conteudo-sutil">
                      {i + 1}.
                    </span>
                    <CampoDeSelecao
                      rotulo="Ação"
                      className="min-w-[16rem] flex-1"
                      value={acao.tipo}
                      ajuda={AJUDA_DA_ACAO[acao.tipo]}
                      onChange={(e) => mudarAcao(i, acaoEmBranco(e.target.value as TipoDeAcao))}
                      opcoes={ACOES.filter(
                        (a) =>
                          (a !== 'COPIAR_CARD' || doCard) &&
                          (a !== 'ENVIAR_EMAIL' || props.semEmail !== true || acao.tipo === a),
                      ).map((a) => ({
                        valor: a,
                        rotulo: ROTULO_DA_ACAO[a],
                      }))}
                    />
                    <div className="flex gap-1 pb-1">
                      <Botao
                        variante="sutil"
                        tamanho="pequeno"
                        aria-label="Subir a ação"
                        disabled={i === 0}
                        onClick={() => {
                          const acoes = [...d.acoes];
                          [acoes[i - 1], acoes[i]] = [acoes[i]!, acoes[i - 1]!];
                          mudar({ acoes });
                        }}
                      >
                        ↑
                      </Botao>
                      <Botao
                        variante="sutil"
                        tamanho="pequeno"
                        aria-label="Descer a ação"
                        disabled={i === d.acoes.length - 1}
                        onClick={() => {
                          const acoes = [...d.acoes];
                          [acoes[i + 1], acoes[i]] = [acoes[i]!, acoes[i + 1]!];
                          mudar({ acoes });
                        }}
                      >
                        ↓
                      </Botao>
                      <Botao
                        variante="sutil"
                        tamanho="pequeno"
                        disabled={d.acoes.length === 1}
                        onClick={() => mudar({ acoes: d.acoes.filter((_, j) => j !== i) })}
                      >
                        Tirar
                      </Botao>
                    </div>
                  </div>
                  <CamposDaAcao
                    acao={acao}
                    doCard={doCard}
                    colunas={colunas}
                    tags={opcoes.data?.tags ?? []}
                    pessoas={opcoes.data?.pessoas ?? []}
                    emails={opcoes.data?.emails ?? []}
                    aoMudar={(nova) => mudarAcao(i, nova)}
                  />
                </li>
              ))}
            </ol>
            {d.acoes.length < 8 && (
              <div className="mt-1">
                <Botao
                  variante="secundario"
                  tamanho="pequeno"
                  onClick={() => mudar({ acoes: [...d.acoes, acaoEmBranco('COMENTAR')] })}
                >
                  + Adicionar ação
                </Botao>
              </div>
            )}
          </Bloco>

          <Bloco titulo="Como o Deca vai executar">
            {descricao.isError ? (
              <Aviso tom="atencao" titulo="Falta acertar">
                {mensagemDoErro(descricao.error)}
              </Aviso>
            ) : descricao.data === undefined ? (
              <p className="text-sm text-conteudo-sutil">Montando a descrição…</p>
            ) : (
              <div className="flex flex-col gap-2">
                <p className="text-sm leading-relaxed text-conteudo-primario">
                  {descricao.data.descricao}
                </p>
                {descricao.data.problemas.map((p) => (
                  <p key={p} className="text-sm text-atencao">
                    {p}
                  </p>
                ))}
              </div>
            )}
          </Bloco>

          <Bloco
            titulo="Corrigir por texto"
            nota="Diga o que mudar e a IA ajusta os campos acima — você confere antes de salvar."
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
              <CampoDeTexto
                rotulo="O que mudar"
                rotuloOculto
                className="flex-1"
                rows={2}
                maxLength={2000}
                placeholder="Ex.: só para o Pontual, e crie também uma tarefa de ligar em 3 dias"
                value={correcao}
                onChange={(e) => setCorrecao(e.target.value)}
              />
              <Botao
                variante="secundario"
                carregando={corrigir.isPending}
                disabled={correcao.trim() === ''}
                onClick={() => corrigir.mutate()}
              >
                Corrigir
              </Botao>
            </div>
            {corrigir.data?.ok === false && (
              <Aviso tom="atencao" titulo="Não deu para ajustar assim">
                {corrigir.data.motivo}
              </Aviso>
            )}
            {corrigir.isError && <ErroDaApi titulo="A IA não respondeu" erro={corrigir.error} />}
          </Bloco>

          {salvar.isError && <ErroDaApi titulo="Não foi possível salvar" erro={salvar.error} />}
          {opcoes.isError && (
            <ErroDaApi titulo="Não foi possível carregar as colunas e tags" erro={opcoes.error} />
          )}
        </div>
      )}
    </Modal>
  );
}

function CamposDaAcao(props: {
  readonly acao: AcaoDaRegra;
  readonly doCard: boolean;
  readonly colunas: readonly { readonly valor: string; readonly rotulo: string }[];
  readonly tags: readonly {
    readonly id: string;
    readonly nome: string;
    readonly deServico: boolean;
  }[];
  readonly pessoas: readonly { readonly accountId: string; readonly nome: string }[];
  readonly emails: readonly { readonly id: string; readonly nome: string }[];
  readonly aoMudar: (acao: AcaoDaRegra) => void;
}): JSX.Element {
  const { acao, aoMudar } = props;
  switch (acao.tipo) {
    case 'ENVIAR_EMAIL':
      return <CamposDoEmail acao={acao} emails={props.emails} aoMudar={aoMudar} />;
    case 'CRIAR_CARD':
    case 'LEVAR_CARD':
    case 'COPIAR_CARD':
      return (
        <CampoDeSelecao
          rotulo="Coluna"
          value={acao.colunaId ?? ''}
          onChange={(e) => aoMudar({ ...acao, colunaId: e.target.value || null })}
          opcoes={[{ valor: '', rotulo: 'Escolha a coluna' }, ...props.colunas]}
        />
      );
    case 'ETIQUETAR':
      return (
        <CampoDeSelecao
          rotulo="Tag"
          value={acao.tagId}
          onChange={(e) => aoMudar({ ...acao, tagId: e.target.value })}
          opcoes={[
            { valor: '', rotulo: 'Escolha a tag' },
            ...(props.doCard
              ? []
              : [{ valor: TAG_DO_SERVICO, rotulo: 'A tag do serviço do evento' }]),
            ...props.tags.map((t) => ({ valor: t.id, rotulo: t.nome })),
          ]}
        />
      );
    case 'COMENTAR':
      return (
        <CampoDeTexto
          rotulo="Comentário"
          rows={3}
          maxLength={2000}
          ajuda={`Campos: ${VARIAVEIS_DO_TEXTO.map((v) => `{${v.chave}} ${v.ajuda}`).join(' · ')}`}
          placeholder="Ex.: Negociação de {valor} concluída no {servico} em {data}."
          value={acao.texto}
          onChange={(e) => aoMudar({ ...acao, texto: e.target.value })}
        />
      );
    case 'CRIAR_ATIVIDADE':
      return (
        <div className="grid gap-4 sm:grid-cols-2">
          <Campo
            rotulo="Título"
            maxLength={200}
            ajuda="Aceita os mesmos campos do comentário, como {empresa}."
            value={acao.titulo}
            onChange={(e) => aoMudar({ ...acao, titulo: e.target.value })}
          />
          <CampoDeSelecao
            rotulo="Tipo"
            value={acao.tipoAtividade}
            onChange={(e) =>
              aoMudar({
                ...acao,
                tipoAtividade: e.target.value as (typeof TIPOS_DE_ATIVIDADE)[number],
              })
            }
            opcoes={TIPOS_DE_ATIVIDADE.map((t) => ({
              valor: t,
              rotulo: ROTULO_DO_TIPO_DE_ATIVIDADE[t],
            }))}
          />
          <Campo
            rotulo="Prazo, em dias"
            type="number"
            min={0}
            max={365}
            ajuda="Contados do dia do evento; 0 é no mesmo dia."
            value={String(acao.prazoEmDias)}
            onChange={(e) =>
              aoMudar({
                ...acao,
                prazoEmDias: Math.max(0, Math.min(365, Math.round(Number(e.target.value) || 0))),
              })
            }
          />
          <CampoDeSelecao
            rotulo="Responsável"
            value={acao.responsavelId ?? ''}
            onChange={(e) => aoMudar({ ...acao, responsavelId: e.target.value || null })}
            opcoes={[
              { valor: '', rotulo: 'Quem fez a ação (ou quem criou a regra)' },
              ...props.pessoas.map((p) => ({ valor: p.accountId, rotulo: p.nome })),
            ]}
          />
        </div>
      );
  }
}

/** "Enviar um e-mail": qual, para quem e quando (D-017). */
function CamposDoEmail(props: {
  readonly acao: Extract<AcaoDaRegra, { tipo: 'ENVIAR_EMAIL' }>;
  readonly emails: readonly { readonly id: string; readonly nome: string }[];
  readonly aoMudar: (acao: AcaoDaRegra) => void;
}): JSX.Element {
  const { acao, aoMudar } = props;
  // O texto dos endereços como a pessoa digita; vira lista a cada mudança.
  const [enderecos, setEnderecos] = useState(acao.para.enderecos.join(', '));
  const mudarPara = (parcial: Partial<ParaQuem>): void =>
    aoMudar({ ...acao, para: { ...acao.para, ...parcial } });

  return (
    <div className="flex flex-col gap-4">
      <CampoDeSelecao
        rotulo="E-mail"
        value={acao.emailId}
        ajuda={
          props.emails.length === 0
            ? 'Nenhum e-mail criado ainda: crie em Configurações › E-mails › + Novo e-mail.'
            : 'O texto e o visual ficam em Configurações › E-mails.'
        }
        onChange={(e) => aoMudar({ ...acao, emailId: e.target.value })}
        opcoes={[
          { valor: '', rotulo: 'Escolha o e-mail' },
          ...props.emails.map((e) => ({ valor: e.id, rotulo: e.nome })),
        ]}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <CampoDeSelecao
          rotulo="Contatos da empresa"
          value={acao.para.contatos}
          ajuda="A empresa do evento: o fornecedor no Pontual, o cliente no Passivo e no Recorrente."
          onChange={(e) =>
            mudarPara({
              contatos: e.target.value as ParaQuem['contatos'],
              funcao: e.target.value === 'FUNCAO' ? (acao.para.funcao ?? '') : null,
            })
          }
          opcoes={[
            { valor: 'TODOS', rotulo: 'Todos os contatos' },
            { valor: 'FUNCAO', rotulo: 'Só os de uma função' },
            { valor: 'NENHUM', rotulo: 'Nenhum' },
          ]}
        />
        {acao.para.contatos === 'FUNCAO' && (
          <Campo
            rotulo="Função"
            list="funcoes-sugeridas"
            maxLength={60}
            placeholder="Ex.: Financeiro"
            ajuda="Parte do cargo cadastrado no contato. Sem ninguém com ela, vai a todos os contatos."
            value={acao.para.funcao ?? ''}
            onChange={(e) => mudarPara({ funcao: e.target.value })}
          />
        )}
        <datalist id="funcoes-sugeridas">
          {FUNCOES_SUGERIDAS.map((f) => (
            <option key={f} value={f} />
          ))}
        </datalist>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 sm:items-start">
        <Marcar
          rotulo="Também o assessor da empresa"
          ajuda="Os contatos do assessor cadastrado no cliente."
          marcada={acao.para.assessor}
          aoMudar={(assessor) => mudarPara({ assessor })}
        />
        <Campo
          rotulo="Endereços fixos"
          placeholder="Ex.: gabriel@ms3capital.com"
          ajuda="Opcional. Separe com vírgula — para receber uma cópia, por exemplo."
          value={enderecos}
          onChange={(e) => {
            setEnderecos(e.target.value);
            mudarPara({
              enderecos: e.target.value
                .split(/[,;\s]+/)
                .map((x) => x.trim())
                .filter((x) => x !== ''),
            });
          }}
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <CampoDeSelecao
          rotulo="Quando enviar"
          value={acao.diasDepois === 0 ? 'agora' : 'depois'}
          onChange={(e) =>
            aoMudar({
              ...acao,
              diasDepois: e.target.value === 'agora' ? 0 : Math.max(1, acao.diasDepois),
            })
          }
          opcoes={[
            { valor: 'agora', rotulo: 'Na hora' },
            { valor: 'depois', rotulo: 'Alguns dias depois' },
          ]}
        />
        {acao.diasDepois > 0 && (
          <Campo
            rotulo="Quantos dias depois"
            type="number"
            min={1}
            max={90}
            ajuda="Sai às 9h desse dia."
            value={String(acao.diasDepois)}
            onChange={(e) =>
              aoMudar({
                ...acao,
                diasDepois: Math.max(1, Math.min(90, Math.round(Number(e.target.value) || 1))),
              })
            }
          />
        )}
      </div>
    </div>
  );
}

function Bloco(props: {
  readonly titulo: string;
  readonly nota?: string;
  readonly children: React.ReactNode;
}): JSX.Element {
  return (
    <section className="flex flex-col gap-3">
      <div>
        <h3 className="text-sm font-semibold uppercase tracking-wide text-conteudo-secundario">
          {props.titulo}
        </h3>
        {props.nota !== undefined && (
          <p className="mt-1 text-xs text-conteudo-sutil">{props.nota}</p>
        )}
      </div>
      {props.children}
    </section>
  );
}

function Marcar(props: {
  readonly rotulo: string;
  readonly ajuda?: string;
  readonly marcada: boolean;
  readonly aoMudar: (marcada: boolean) => void;
}): JSX.Element {
  return (
    <label className="flex cursor-pointer items-start gap-2 py-2 text-sm text-conteudo-primario">
      <input
        type="checkbox"
        className="mt-0.5 h-4 w-4 accent-acento-indigo"
        checked={props.marcada}
        onChange={(e) => props.aoMudar(e.target.checked)}
      />
      <span className="flex flex-col">
        {props.rotulo}
        {props.ajuda !== undefined && (
          <span className="text-xs text-conteudo-sutil">{props.ajuda}</span>
        )}
      </span>
    </label>
  );
}
